import { useState } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

export default function ContactCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation({ threshold: 0.1 });
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation({ threshold: 0.1 });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setError('请填写姓名、邮箱和留言内容');
      return;
    }
    if (formData.message.length > 500) {
      setError('留言内容不能超过500字');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      const res = await fetch('https://readdy.ai/api/form/d6ft6o2au1b90a4046pg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('提交失败，请稍后重试');
      }
    } catch {
      setError('网络错误，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-10 md:py-20 lg:py-24 relative overflow-hidden bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800">
      {/* Decorative Background */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-start">

          {/* Left: Info */}
          <div
            ref={leftRef}
            className="text-white"
            style={{
              transition: 'opacity 0.7s ease, transform 0.7s ease',
              opacity: leftVisible ? 1 : 0,
              transform: leftVisible ? 'translateX(0)' : 'translateX(-48px)',
            }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold mb-3 md:mb-6 leading-tight">
              <span className="italic">准备好开启<br />智能化转型了吗？</span>
            </h2>
            <p className="text-sm md:text-lg text-white/85 mb-5 md:mb-10 font-light leading-relaxed">
              我们的专家团队随时为您提供咨询与技术支持，欢迎填写右侧表单，我们将在1个工作日内与您联系。
            </p>

            <div className="space-y-3.5 md:space-y-5 mb-5 md:mb-10">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                  <i className="ri-phone-line text-sm md:text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-white/60 mb-0.5">联系电话</p>
                  <a href="tel:18159811888" className="text-sm font-medium hover:text-white/80 transition-colors">18159811888</a>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                  <i className="ri-mail-line text-sm md:text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-white/60 mb-0.5">电子邮箱</p>
                  <a href="mailto:yanhp@fjlcwl.com" className="text-sm font-medium hover:text-white/80 transition-colors break-all">yanhp@fjlcwl.com</a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center bg-white/20 rounded-full flex-shrink-0">
                  <i className="ri-map-pin-line text-sm md:text-lg"></i>
                </div>
                <div>
                  <p className="text-xs text-white/60 mb-0.5">公司地址</p>
                  <span className="text-sm font-medium leading-relaxed">福建省龙岩市新罗区东肖经济开发区<br />曲潭路15号科技创业园4号楼10楼</span>
                </div>
              </div>
            </div>

            <div className="flex gap-2.5 mb-5 lg:mb-0">
              <a href="#" className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer">
                <i className="ri-wechat-line text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer">
                <i className="ri-weibo-line text-sm"></i>
              </a>
              <a href="#" className="w-8 h-8 flex items-center justify-center bg-white/20 rounded-full hover:bg-white/30 transition-colors cursor-pointer">
                <i className="ri-linkedin-line text-sm"></i>
              </a>
            </div>

            {/* Map Embed */}
            <div className="mt-5 md:mt-8 rounded-xl overflow-hidden border border-white/20 shadow-lg">
              <div className="bg-white/10 px-3 py-2 flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-2-line text-white text-xs"></i>
                </div>
                <span className="text-white text-xs font-medium">公司位置导航</span>
                <a
                  href="https://api.map.baidu.com/marker?location=25.049232,117.020620&title=福建龙创未来股份有限公司&content=福建省龙岩市新罗区东肖经济开发区曲潭路15号科技创业园4号楼10楼&output=html&src=webapp.baidu.openAPIdemo"
                  target="_blank"
                  rel="nofollow noopener noreferrer"
                  className="ml-auto text-white/70 hover:text-white text-xs flex items-center gap-1 transition-colors cursor-pointer whitespace-nowrap"
                >
                  在百度地图中打开 <i className="ri-external-link-line"></i>
                </a>
              </div>
              <div className="w-full h-36 md:h-52">
                <iframe
                  title="公司地址地图 - 福建省龙岩市新罗区科技创业园"
                  width="100%"
                  height="100%"
                  style={{ border: 0, display: 'block' }}
                  loading="lazy"
                  allowFullScreen
                  srcDoc={`<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width,initial-scale=1">
<style>html,body,#map{margin:0;padding:0;width:100%;height:100%}</style>
<script src="https://api.map.baidu.com/api?v=3.0&ak=E4805d16520de693a3fe707cdc962045&type=webgl"></script>
</head>
<body>
<div id="map"></div>
<script>
var map=new BMapGL.Map("map");
var point=new BMapGL.Point(117.020620,25.049232);
map.centerAndZoom(point,16);
map.enableScrollWheelZoom(true);
map.addControl(new BMapGL.ZoomControl());
var marker=new BMapGL.Marker(point);
map.addOverlay(marker);
var label=new BMapGL.Label("福建龙创未来股份有限公司",{offset:new BMapGL.Size(10,-10)});
label.setStyle({color:"#333",fontSize:"12px",padding:"4px 8px",borderRadius:"4px",border:"1px solid #ccc",backgroundColor:"#fff",whiteSpace:"nowrap"});
marker.setLabel(label);
</script>
</body>
</html>`}
                ></iframe>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div
            ref={rightRef}
            className="bg-white rounded-2xl p-5 md:p-8 shadow-2xl"
            style={{
              transition: 'opacity 0.7s ease 150ms, transform 0.7s ease 150ms',
              opacity: rightVisible ? 1 : 0,
              transform: rightVisible ? 'translateX(0)' : 'translateX(48px)',
            }}
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-teal-100 rounded-full mb-3">
                  <i className="ri-check-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-800 mb-2">提交成功！</h3>
                <p className="text-gray-500 text-sm">感谢您的留言，我们将在1个工作日内与您联系。</p>
              </div>
            ) : (
              <>
                <h3 className="text-base md:text-xl font-bold text-gray-800 mb-4 md:mb-6">联系我们</h3>
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-3.5 md:space-y-4"
                >
                  <div className="grid grid-cols-2 gap-3 md:gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">姓名 <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="您的姓名"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">电话</label>
                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="联系电话"
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">邮箱 <span className="text-red-500">*</span></label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="您的邮箱地址"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">公司名称</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="您所在的公司"
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500 transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">感兴趣的业务</label>
                    <select
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500 transition-colors bg-white cursor-pointer"
                    >
                      <option value="">请选择业务方向</option>
                      <option value="算力投资运营">算力投资运营</option>
                      <option value="人工智能产品装备研发">人工智能产品装备研发</option>
                      <option value="工业数字化">工业数字化</option>
                      <option value="智慧城市">智慧城市</option>
                      <option value="系统集成">系统集成</option>
                      <option value="戴尔服务器代理">戴尔服务器代理</option>
                      <option value="硬件销售">硬件销售</option>
                      <option value="集团硬件供应链平台">集团硬件供应链平台</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      留言内容 <span className="text-red-500">*</span>
                      <span className="text-gray-400 ml-1">（最多500字）</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="请描述您的需求或问题..."
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-teal-500 transition-colors resize-none"
                    />
                    <p className="text-xs text-gray-400 text-right mt-0.5">{formData.message.length}/500</p>
                  </div>

                  {error && (
                    <p className="text-xs text-red-500 flex items-center gap-1">
                      <i className="ri-error-warning-line"></i>{error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-2.5 md:py-3 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-60 cursor-pointer whitespace-nowrap"
                  >
                    {submitting ? '提交中...' : '提交留言'}
                  </button>
                </form>
              </>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}